"use client"

import { useEffect, useRef } from "react"
import { X } from "lucide-react"
import { ButtonFluxi } from "../common/Buttons/ButtonFluxi/ButtonFluxi"
import { Loader2 } from "lucide-react"

interface BaseModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  description: string
  imageSrc?: string
  imageAlt?: string
  buttonOne?: {
    text: string
    onClick: () => void
    disabled?: boolean
    loading?: boolean
    variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link"
    className?: string
  }
  buttonTwo?: {
    text: string
    onClick: () => void
    disabled?: boolean
    loading?: boolean
    variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link"
    className?: string
  }
  showCloseButton?: boolean
}

export function BaseModal({
  isOpen,
  onClose,
  title,
  description,
  imageSrc,
  imageAlt = "Modal image",
  buttonOne,
  buttonTwo,
  showCloseButton = true
}: BaseModalProps) {
  const modalRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div 
        ref={modalRef}
        className="bg-white rounded-lg sm:rounded-xl shadow-xl max-w-md w-full mx-2 sm:mx-4 relative max-h-[90vh] overflow-y-auto"
      >
        {/* Close button */}
        {showCloseButton && (
          <button
            onClick={onClose}
            className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10 p-1.5 sm:p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
          </button>
        )}

        <div className="p-4 sm:p-6">
          {/* Image/GIF/Video */}
          {imageSrc && (
            <div className="flex justify-center mb-3 sm:mb-4">
              <div className="relative">
                {imageSrc.endsWith('.webm') || imageSrc.endsWith('.mp4') ? (
                  <video
                    src={imageSrc}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="max-w-full h-auto max-h-32 sm:max-h-48 object-contain"
                  />
                ) : (
                  <img
                    src={imageSrc}
                    alt={imageAlt}
                    className="max-w-full h-auto max-h-32 sm:max-h-48 object-contain"
                  />
                )}
              </div>
            </div>
          )}

          {/* Title */}
          <h2 className="text-lg sm:text-xl font-bold text-center text-gray-900 mb-2 sm:mb-4">
            {title}
          </h2>

          {/* Description */}
          <p className="text-sm sm:text-base text-gray-600 text-center mb-6 sm:mb-8 leading-relaxed">
            {description}
          </p>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            {buttonOne && (
              <ButtonFluxi 
                text={buttonOne.loading ? "" : buttonOne.text}
                onClick={buttonOne.onClick} 
                disabled={buttonOne.disabled} 
                isEffect={false} 
                fullWidth={true}
              >
                {buttonOne.loading && <Loader2 className="w-4 h-4 animate-spin" />}
              </ButtonFluxi>
            )}
            
            {buttonTwo && (
              <ButtonFluxi 
                text={buttonTwo.loading ? "" : buttonTwo.text}
                onClick={buttonTwo.onClick} 
                disabled={buttonTwo.disabled} 
                isEffect={false} 
                fullWidth={true} 
                style={{ background: "#ffffff", boxShadow: "none" }}
                border_color="#595EEE"
                color="#595EEE" 
              >
                {buttonTwo.loading && <Loader2 className="w-4 h-4 animate-spin" />}
              </ButtonFluxi>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}